import { type BugScreenshot, useReportBug } from '@shkola/api';
import { useRouterState } from '@tanstack/react-router';
import { Bug, X } from 'lucide-react';
import { type ClipboardEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../components/ui';
import { INK, INK_SOFT, LINE, PAPER, pillClass } from '../design/forms';
import { useCurrentContext } from './ViewContext';

/*
 * «Повідомити про помилку» on every page of every role (29.10, his «кнопка баг-репорта везде — ребята
 * будут тестировать… прикрепить скриншоты и отправлять нам»): a round button in the bar by the bell; a
 * dialog for the words and up to six screenshots (chosen, dropped or pasted). The page, the role, the
 * browser and the screen go with it by themselves.
 */

const MAX = 6;
const MAX_BYTES = 5 * 1024 * 1024;
const EXT: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
  'image/heic': 'heic',
};

export function BugButton({ className }: { className: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        aria-label={t('bugs.button')}
        title={t('bugs.button')}
        onClick={() => setOpen(true)}
        className={className}
      >
        <Bug size={17} strokeWidth={1.8} aria-hidden />
      </button>
      {open && <BugDialog onClose={() => setOpen(false)} />}
    </>
  );
}

function BugDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const location = useRouterState({ select: (s) => s.location });
  const report = useReportBug();
  const [text, setText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [warning, setWarning] = useState<string>();
  const input = useRef<HTMLInputElement>(null);
  const previews = useObjectUrls(files);

  const add = (list: Iterable<File>) => {
    setWarning(undefined);
    const fresh = [...list].filter((f) => f.type in EXT);
    if (fresh.some((f) => f.size > MAX_BYTES)) setWarning(t('bugs.tooBig'));
    setFiles((now) => [...now, ...fresh.filter((f) => f.size <= MAX_BYTES)].slice(0, MAX));
  };
  const onPaste = (e: ClipboardEvent) => {
    const pasted = [...e.clipboardData.files];
    if (pasted.length) add(pasted);
  };
  const send = () => {
    const screenshots: BugScreenshot[] = files.map((f) => ({
      file: f,
      extension: EXT[f.type] ?? 'png',
      contentType: f.type,
    }));
    report.mutate({
      ctx,
      platform: 'web',
      appVersion: import.meta.env.MODE,
      page: `${location.pathname}${location.searchStr ?? ''}`,
      device: `${navigator.userAgent} · ${window.innerWidth}×${window.innerHeight}`,
      description: text,
      screenshots,
    });
  };

  if (report.isSuccess)
    return (
      <Modal title={t('bugs.title')} onClose={onClose}>
        <p className="text-[15px] leading-6" style={{ color: INK }}>
          {t('bugs.sent')}
        </p>
        <div className="mt-5 flex flex-wrap gap-2">
          <button type="button" className={`${pillClass('dark')} !pr-5`} onClick={onClose}>
            {t('common.close')}
          </button>
          <button
            type="button"
            className={`${pillClass('light')} !pr-5`}
            onClick={() => {
              report.reset();
              setText('');
              setFiles([]);
            }}
          >
            {t('bugs.another')}
          </button>
        </div>
      </Modal>
    );

  return (
    <Modal title={t('bugs.title')} onClose={onClose}>
      <div className="flex flex-col gap-4" onPaste={onPaste}>
        <p className="text-[14px] leading-5" style={{ color: INK_SOFT }}>
          {t('bugs.lead')}
        </p>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t('bugs.placeholder')}
          maxLength={4000}
          rows={5}
          className="w-full resize-y rounded-[16px] p-3.5 text-[15px] leading-6 outline-none focus:ring-2 focus:ring-[#241A45]/30"
          style={{ border: `1px solid ${LINE}`, color: INK }}
        />
        <div
          className="flex flex-col gap-2"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            add(e.dataTransfer.files);
          }}
        >
          <p className="text-[14px] font-medium" style={{ color: INK }}>
            {t('bugs.screenshots')}
          </p>
          <div className="flex flex-wrap gap-2">
            {previews.map((url, i) => (
              <span key={url} className="relative">
                <img
                  src={url}
                  alt=""
                  className="size-20 rounded-[14px] object-cover"
                  style={{ border: `1px solid ${LINE}` }}
                />
                <button
                  type="button"
                  aria-label={t('bugs.remove')}
                  onClick={() => setFiles((now) => now.filter((_, j) => j !== i))}
                  className="absolute -top-2 -right-2 flex size-6 items-center justify-center rounded-full bg-white"
                  style={{ boxShadow: `inset 0 0 0 1px ${LINE}`, color: INK }}
                >
                  <X size={13} aria-hidden />
                </button>
              </span>
            ))}
            {files.length < MAX && (
              <button
                type="button"
                onClick={() => input.current?.click()}
                className="flex size-20 items-center justify-center rounded-[14px] px-2 text-center text-[12px] leading-4 font-medium transition-colors hover:bg-[#E9E5DC]"
                style={{ backgroundColor: PAPER, color: INK }}
              >
                {t('bugs.add')}
              </button>
            )}
          </div>
          <input
            ref={input}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/heic"
            multiple
            hidden
            onChange={(e) => {
              if (e.target.files) add(e.target.files);
              e.target.value = '';
            }}
          />
          <p className="text-[12.5px]" style={{ color: INK_SOFT }}>
            {t('bugs.paste')} · {t('bugs.limit')}
          </p>
        </div>
        {(warning || report.isError) && (
          <p className="text-[13.5px]" style={{ color: '#B4431C' }}>
            {report.isError ? t('bugs.failed') : warning}
          </p>
        )}
        <div>
          <button
            type="button"
            className={`${pillClass('dark')} !pr-5 disabled:opacity-50`}
            disabled={text.trim().length < 3 || report.isPending}
            onClick={send}
          >
            {report.isPending ? t('bugs.sending') : t('bugs.send')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** Object URLs for the chosen files, revoked when they go. */
function useObjectUrls(files: File[]) {
  const [urls, setUrls] = useState<string[]>([]);
  useEffect(() => {
    const next = files.map((f) => URL.createObjectURL(f));
    setUrls(next);
    return () => next.forEach((u) => URL.revokeObjectURL(u));
  }, [files]);
  return urls;
}
